import { useNavigate } from 'react-router-dom';
import { BookOpen, Users, Zap, Globe, MessageSquare, Cpu, ArrowRight, Sparkles, GraduationCap, TrendingUp, Lightbulb, Building2, Wheat, Train, UserCheck, Rocket, Brain, BarChart3, Heart, Briefcase, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLang } from '../contexts/LangContext';
import { PageSEO } from '../components/PageSEO';

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_339bb7ce-7a36-4747-83a9-e81691bc413d/artifacts/6zlvaodd_FC794F62-3E36-4467-8EBF-7CC126622473.png";
const AFII_LOGO = "https://customer-assets.emergentagent.com/job_afii-future/artifacts/zz1rxbi3_image.png";

const V2_PILLARS = [
  { icon: BookOpen, title: 'Knowledge', desc: 'Practical learning for the real world. Deep insights across critical sectors.', color: '#D4A843' },
  { icon: Lightbulb, title: 'Innovation & Technology', desc: 'Solving African challenges with global impact. AI, fintech, and digital systems.', color: '#3B82F6' },
  { icon: TrendingUp, title: 'Economic Growth', desc: 'Creating systems that drive wealth, opportunity, and sustainable prosperity.', color: '#10B981' },
  { icon: Building2, title: 'Infrastructure', desc: 'Building today for a connected tomorrow. Transport, energy, and digital networks.', color: '#8B5CF6' },
  { icon: Wheat, title: 'Agriculture & Food Security', desc: 'Sustainable solutions for a strong foundation. Feeding the continent intelligently.', color: '#F59E0B' },
  { icon: Train, title: 'Trade & Connectivity', desc: 'Connecting Africa to the world and the world to Africa. AfCFTA and beyond.', color: '#06B6D4' },
  { icon: Users, title: 'People & Leadership', desc: 'Empowering minds to build a better Africa. Human capital is the ultimate resource.', color: '#EC4899' },
  { icon: Rocket, title: 'Opportunity Access', desc: 'Connect with talent, jobs, and collaborations. From ideas to systems.', color: '#D4A843' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useLang();

  return (
    <div className="min-h-screen bg-white">
      <PageSEO
        title="Knowledge. Strategy. Systems. Prosperity"
        description="AFII is a free, open learning and intelligence platform built to strengthen knowledge, strategy, systems thinking, and opportunity across Africa and beyond. Powered by Grace, your AI guide."
        path="/"
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* HERO — Static, full-screen, image as poster */}
      <section className="relative overflow-hidden flex flex-col justify-end" style={{ minHeight: 'calc(100vh - 57px)', minHeight: 'calc(100dvh - 57px)', background: 'linear-gradient(180deg, #f8fbff 0%, #dfefff 45%, #06142d 100%)' }}>
        {/* Background image — static poster, no animation */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${HERO_IMAGE})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center top',
            transform: 'none',
            animation: 'none',
          }}
          data-testid="hero-v2-image"
          role="img"
          aria-label="AFII — African Future Intelligence Initiative"
        />

        {/* CTAs only — bottom of hero, not blocking artwork */}
        <div className="absolute bottom-8 left-0 right-0 z-10 flex flex-wrap justify-center gap-3 px-4">
          <Button data-testid="hero-start-btn" onClick={() => navigate('/courses')} className="cursor-pointer font-bold hover:-translate-y-0.5 rounded-full px-7 py-3.5 text-base transition-all duration-300 ease-out active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[#f5c542]" style={{ background: '#f5c542', color: '#071832', fontWeight: 800, boxShadow: '0 12px 30px rgba(245,197,66,0.35)' }}>
            Start Learning <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          <button data-testid="hero-grace-btn" onClick={() => navigate('/chat')} className="cursor-pointer inline-flex items-center justify-center rounded-full px-7 py-3.5 text-base font-['Manrope'] text-white transition-all duration-300 ease-out hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[#f5c542]" style={{ background: 'rgba(7,24,50,0.82)', fontWeight: 700, border: '1px solid rgba(255,255,255,0.25)', boxShadow: '0 6px 20px rgba(0,0,0,0.25)' }} onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(245,197,66,0.75)'; e.currentTarget.style.boxShadow = '0 0 28px rgba(245,197,66,0.35)'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.25)'; }}>
            <MessageSquare className="mr-2 w-4 h-4" /> Meet Grace
          </button>
          <button onClick={() => navigate('/employers')} className="cursor-pointer inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-['Manrope'] text-white transition-all duration-300 ease-out hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[#f5c542]" style={{ background: 'rgba(7,24,50,0.82)', fontWeight: 700, border: '1px solid rgba(255,255,255,0.25)', boxShadow: '0 6px 20px rgba(0,0,0,0.25)' }} onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(245,197,66,0.75)'; e.currentTarget.style.boxShadow = '0 0 28px rgba(245,197,66,0.35)'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.25)'; }}>
            <Briefcase className="mr-2 w-4 h-4" /> For Employers
          </button>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* CORE FOCUS AREAS — Knowledge. Strategy. Systems. Prosperity. */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <p className="font-['Manrope'] font-bold text-sm uppercase tracking-[0.25em] text-[#D4A843] mb-4">Our Core Focus Areas</p>
            <h2 className="font-['Outfit'] font-extrabold text-3xl sm:text-4xl md:text-5xl text-[#0B1D3A]">Knowledge. Strategy. Systems. Prosperity.</h2>
            <p className="font-['Manrope'] mt-4 text-base text-gray-500 max-w-3xl mx-auto leading-relaxed">AFII helps transform learning into capability, capability into opportunity, and opportunity into shared prosperity.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { title: 'Knowledge', text: 'Practical learning for the real world. Deep insights across critical sectors.', icon: BookOpen, color: '#D4A843' },
              { title: 'Strategy', text: 'Better thinking for better decisions. Scenario planning and systems analysis.', icon: Brain, color: '#3B82F6' },
              { title: 'Systems', text: 'Understanding how economies, institutions, and technologies work together.', icon: Cpu, color: '#10B981' },
              { title: 'Prosperity', text: 'Turning knowledge into opportunity, opportunity into lasting impact.', icon: TrendingUp, color: '#8B5CF6' },
              { title: 'Opportunity Access', text: 'Connecting learners to jobs, mentors, and capital across Africa.', icon: Rocket, color: '#F59E0B' },
              { title: 'Grace AI', text: 'An intelligent guide for learning, building, and strategic clarity.', icon: Sparkles, color: '#D4A843' },
              { title: 'Employers & Talent', text: "Bridging Africa's talent with real opportunities through skill-based hiring.", icon: Briefcase, color: '#06B6D4' },
              { title: 'Support AFII', text: 'Help expand free access, deeper content, and broader impact across the continent.', icon: Heart, color: '#EC4899' },
            ].map(({ title, text, icon: Icon, color }, i) => (
              <div key={i} className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${color}12` }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <h3 className="font-['Outfit'] font-bold text-[#0B1D3A] text-base mb-2">{title}</h3>
                <p className="font-['Manrope'] text-gray-400 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* THE MIND IS THE GREATEST RESOURCE */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-[#0B1D3A] relative overflow-hidden">
        {/* Subtle Africa outline */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
          <svg viewBox="0 0 800 800" className="absolute right-0 top-1/2 -translate-y-1/2 w-[500px] h-[500px]" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M400 80c-20 10-50 30-70 60-15 25-30 55-35 90-8 55 5 80-10 120-20 55-50 70-45 130 5 50 30 80 20 130-8 40-25 60-15 90 12 35 45 50 80 60 40 12 70 5 110 15 35 8 60 25 90 15 25-8 40-30 60-55 25-30 35-65 50-100 20-45 25-85 15-130-8-35-25-60-20-95 5-30 20-55 30-85 12-35 15-70 5-105-8-25-20-45-35-60-20-20-45-30-70-35-30-8-60-10-90-15-25-5-45-10-70-30z" stroke="#D4A843" strokeWidth="1.5"/>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <div className="text-center mb-14">
            <p className="font-['Manrope'] font-semibold text-xs text-[#D4A843] tracking-[0.3em] uppercase mb-4">Africa's Untapped Potential</p>
            <h2 className="font-['Outfit'] font-bold text-3xl sm:text-4xl text-white mb-6">The Mind Is the Greatest Resource</h2>
            <p className="font-['Manrope'] text-white/60 text-base leading-relaxed max-w-2xl mx-auto mb-3">
              Africa is rich in resources, culture, youth, creativity, and possibility. But the continent's greatest resource is not beneath the ground — it is within the mind.
            </p>
            <p className="font-['Manrope'] text-white/45 text-sm leading-relaxed max-w-xl mx-auto">
              AFII exists to help turn knowledge into systems, systems into opportunity, and opportunity into shared prosperity.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Crown, title: 'Natural Resources', stat: 'Significant global share', desc: 'Major source of gold, platinum, diamonds, cobalt, lithium, and arable land — the raw materials of the modern economy.', color: '#D4A843' },
              { icon: Users, title: 'Youth & Talent', stat: 'Youngest population globally', desc: 'By 2050, one in four people on Earth will be African. The question is not numbers — it is capability.', color: '#3B82F6' },
              { icon: Cpu, title: 'Digital Growth', stat: 'Rapidly expanding markets', desc: 'Fintech, mobile money, e-commerce, and digital services are growing faster in Africa than nearly anywhere else.', color: '#10B981' },
              { icon: Building2, title: 'Infrastructure Opportunity', stat: 'Generational build cycle', desc: 'Transport, energy, housing, and connectivity — Africa is building what other continents built over a century, in a generation.', color: '#8B5CF6' },
              { icon: Wheat, title: 'Agriculture & Food Security', stat: '60% of uncultivated arable land', desc: 'The world will need to feed 10 billion people. Africa holds a disproportionate share of the land to do it.', color: '#F59E0B' },
              { icon: Globe, title: 'Trade & Connectivity', stat: 'AfCFTA — largest free trade area', desc: 'The African Continental Free Trade Area connects 1.3 billion people across 54 nations into one economic bloc.', color: '#06B6D4' },
            ].map((card, i) => (
              <div key={i} className="p-6 rounded-2xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] transition-colors duration-300">
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${card.color}15` }}>
                    <card.icon className="w-5 h-5" style={{ color: card.color }} />
                  </div>
                  <div>
                    <h3 className="font-['Outfit'] font-bold text-white text-base mb-0.5">{card.title}</h3>
                    <p className="font-['Manrope'] text-xs font-medium" style={{ color: card.color }}>{card.stat}</p>
                  </div>
                </div>
                <p className="font-['Manrope'] text-white/45 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>

          <p className="font-['Manrope'] text-white/20 text-[10px] text-center mt-8 max-w-lg mx-auto">
            Indicative figures based on public development and commodity reports. Exact shares vary by source and year.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* PILLARS — 8 premium cards */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-14">
            <p className="font-['Manrope'] font-semibold text-xs text-[#D4A843] tracking-[0.3em] uppercase mb-4">Intelligence Drives Transformation</p>
            <h2 className="font-['Outfit'] font-bold text-3xl sm:text-4xl text-[#0B1D3A] mb-4">Eight Pillars of African Intelligence</h2>
            <p className="font-['Manrope'] text-gray-500 text-base max-w-2xl mx-auto">AFII covers the knowledge areas that matter most for Africa's transformation — from technology and trade to agriculture and leadership.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {V2_PILLARS.map((pillar, i) => (
              <div
                key={i}
                data-testid={`pillar-${i}`}
                className="group p-6 rounded-2xl border border-gray-100 bg-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${pillar.color}12` }}>
                  <pillar.icon className="w-6 h-6" style={{ color: pillar.color }} />
                </div>
                <h3 className="font-['Outfit'] font-bold text-[#0B1D3A] text-base mb-2">{pillar.title}</h3>
                <p className="font-['Manrope'] text-gray-400 text-sm leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* WHAT AFII DOES */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-['Manrope'] font-semibold text-xs text-[#D4A843] tracking-[0.3em] uppercase mb-4">What AFII Does</p>
              <h2 className="font-['Outfit'] font-bold text-3xl sm:text-4xl text-[#0B1D3A] mb-6">Free learning. AI guidance. Real opportunity.</h2>
              <p className="font-['Manrope'] text-gray-500 text-base leading-relaxed mb-8">
                AFII provides free, structured courses across AI, economics, fintech, futurism, and more — guided by Grace, your AI learning companion. Whether you're a student in Lagos, a professional in Nairobi, or a diaspora builder in London, AFII meets you where you are.
              </p>
              <div className="space-y-4">
                {[
                  { icon: BookOpen, text: 'Free courses across Foundation, Applied, and Strategic tiers' },
                  { icon: Sparkles, text: 'Grace — your AI guide for learning, building, and fundraising' },
                  { icon: BarChart3, text: 'Progress tracking, skill badges, and career development' },
                  { icon: Briefcase, text: 'Employer connections, job board, and talent mobility' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#D4A843]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-[#D4A843]" />
                    </div>
                    <p className="font-['Manrope'] text-gray-600 text-sm">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-[#0B1D3A] rounded-2xl p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4A843] to-[#F5D78E] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-[#D4A843]/20">
                  <Sparkles className="w-10 h-10 text-[#0B1D3A]" />
                </div>
                <h3 className="font-['Outfit'] font-bold text-white text-xl mb-2">Meet Grace</h3>
                <p className="font-['Manrope'] text-white/50 text-sm mb-6">Your AI Intelligence Guide. Ask her anything about learning, building, or fundraising.</p>
                <Button onClick={() => navigate('/chat')} className="bg-[#D4A843] text-[#0B1D3A] font-bold hover:bg-[#F5D78E] rounded-full px-8 py-5">
                  Chat with Grace <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* LEARNING TRACKS */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-14">
            <p className="font-['Manrope'] font-semibold text-xs text-[#D4A843] tracking-[0.3em] uppercase mb-4">{t('courses.eyebrow')}</p>
            <h2 className="font-['Outfit'] font-bold text-3xl sm:text-4xl text-[#0B1D3A] mb-4">Learning Tracks</h2>
            <p className="font-['Manrope'] text-gray-500 text-base max-w-2xl mx-auto">Structured pathways from foundational knowledge to strategic mastery. All free.</p>
          </div>

          {/* Tier explanation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {[
              { tier: 'Foundation', color: '#10B981', desc: 'Core concepts and fundamentals. Start here.', badge: 'bg-emerald-100 text-emerald-800' },
              { tier: 'Applied', color: '#D4A843', desc: 'Case studies, exercises, and professional practice.', badge: 'bg-amber-100 text-amber-800' },
              { tier: 'Strategic', color: '#8B5CF6', desc: 'Policy thinking, simulations, and institutional-grade content.', badge: 'bg-purple-100 text-purple-800' },
            ].map((t, i) => (
              <div key={i} className="p-5 rounded-xl border border-gray-100 bg-white text-center">
                <span className={`inline-block text-xs font-semibold rounded-full px-3 py-1 mb-3 ${t.badge}`}>{t.tier}</span>
                <p className="font-['Manrope'] text-gray-400 text-sm">{t.desc}</p>
              </div>
            ))}
          </div>

          {/* Track cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'AI & Machine Learning', icon: Cpu, count: '6 courses', color: '#D4A843' },
              { name: 'Fintech & Digital Economy', icon: TrendingUp, count: '2 courses', color: '#3B82F6' },
              { name: 'Economics & Finance', icon: BookOpen, count: '2 courses', color: '#10B981' },
              { name: 'Futurism & Innovation', icon: Sparkles, count: '1 course', color: '#8B5CF6' },
              { name: 'Business & Entrepreneurship', icon: Zap, count: '3 courses', color: '#F59E0B' },
              { name: 'Diaspora & Community', icon: Users, count: '2 courses', color: '#EC4899' },
            ].map((track, i) => (
              <button
                key={i}
                data-testid={`track-card-${i}`}
                onClick={() => navigate('/courses')}
                className="flex items-center gap-4 p-5 rounded-xl bg-white border border-gray-100 hover:border-[#D4A843]/30 hover:shadow-md transition-all duration-200 text-left group"
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${track.color}12` }}>
                  <track.icon className="w-5 h-5" style={{ color: track.color }} />
                </div>
                <div>
                  <p className="font-['Outfit'] font-semibold text-[#0B1D3A] text-sm group-hover:text-[#D4A843] transition-colors">{track.name}</p>
                  <p className="font-['Manrope'] text-gray-400 text-xs">{track.count}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button onClick={() => navigate('/courses')} className="bg-[#0B1D3A] text-white font-semibold hover:bg-[#0B1D3A]/90 rounded-full px-10 py-5">
              Explore All Courses <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* EMPLOYER / TALENT MOBILITY */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-[#0B1D3A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-['Manrope'] font-semibold text-xs text-[#D4A843] tracking-[0.3em] uppercase mb-4">AFII Talent Mobility</p>
              <h2 className="font-['Outfit'] font-bold text-3xl sm:text-4xl text-white mb-6">Find, assess, and hire Africa's future talent.</h2>
              <p className="font-['Manrope'] text-white/50 text-base leading-relaxed mb-8">
                Post roles, collect applications through structured questionnaires, and shortlist candidates with skill-based matching. Start free.
              </p>
              <div className="space-y-3">
                {['Post jobs and receive structured applications', 'Skill-based candidate matching', 'Scored questionnaires with knockout filtering', 'Subscription plans from free to enterprise'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#D4A843]/20 flex items-center justify-center flex-shrink-0">
                      <ArrowRight className="w-3 h-3 text-[#D4A843]" />
                    </div>
                    <p className="font-['Manrope'] text-white/60 text-sm">{item}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Button onClick={() => navigate('/employers')} className="bg-[#D4A843] text-[#0B1D3A] font-bold hover:bg-[#F5D78E] rounded-full px-8 py-5">
                  Employer Portal <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Job Postings', value: 'Unlimited', desc: 'on Enterprise plan' },
                { label: 'Applicant Pipeline', value: 'Structured', desc: 'from submission to hire' },
                { label: 'Skill Tags', value: '30+', desc: 'across all sectors' },
                { label: 'Screening', value: 'Built-in', desc: 'questionnaires + scoring' },
              ].map((stat, i) => (
                <div key={i} className="p-5 rounded-xl bg-white/[0.04] border border-white/[0.08]">
                  <p className="font-['Outfit'] font-bold text-[#D4A843] text-xl">{stat.value}</p>
                  <p className="font-['Manrope'] text-white/70 text-sm font-medium">{stat.label}</p>
                  <p className="font-['Manrope'] text-white/30 text-xs mt-1">{stat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* SUPPORT THE INITIATIVE */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="font-['Manrope'] font-semibold text-xs text-[#D4A843] tracking-[0.3em] uppercase mb-4">Support the Initiative</p>
          <h2 className="font-['Outfit'] font-bold text-3xl sm:text-4xl text-[#0B1D3A] mb-6">Help us build the intelligence layer for a continent.</h2>
          <p className="font-['Manrope'] text-gray-500 text-base leading-relaxed mb-8 max-w-2xl mx-auto">
            AFII is independently built and freely accessible. Donations and sponsorships help expand courses, improve translations, grow outreach, and make Grace smarter. Every contribution strengthens the system.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={() => navigate('/sponsor')} className="bg-[#D4A843] text-[#0B1D3A] font-bold hover:bg-[#F5D78E] rounded-full px-8 py-5">
              <Heart className="mr-2 w-4 h-4" /> Support AFII
            </Button>
            <Button onClick={() => navigate('/contact')} variant="outline" className="border-[#0B1D3A]/20 text-[#0B1D3A] hover:bg-[#0B1D3A]/5 rounded-full px-8 py-5">
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* MISSION STRIP — pre-footer, no duplication */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <section className="py-10 bg-[#0B1D3A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="font-['Outfit'] font-bold text-white/90 text-lg md:text-xl tracking-tight mb-3" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.3)' }}>
            Intelligent Minds. Strong Systems. Sustainable Growth. Shared Prosperity.
          </p>
          <p className="font-['Manrope'] text-white/30 text-xs">
            The African Future Intelligence Initiative — free, open, and built to last.
          </p>
        </div>
      </section>

      {/* Temporary deployment verification marker — remove after confirming production serves V2 */}
      <div data-testid="v2-build-marker" className="py-2 text-center bg-gray-50 border-t border-gray-100">
        <p className="font-['Manrope'] text-gray-300 text-[10px] tracking-wider uppercase">AFII V2 Build Check — 2026-05-17T16:20:00Z — pymongo-fix</p>
      </div>
    </div>
  );
}
